<script setup>
import { computed } from 'vue';
import Icon from 'next/icon/Icon.vue';
import { useI18n } from 'vue-i18n';
import { useMessageContext } from './provider.js';
import { hasOneDayPassed } from 'shared/helpers/timeHelper';
import { ORIENTATION, MESSAGE_STATUS } from './constants';

defineProps({
  error: { type: String, required: true },
});

const emit = defineEmits(['retry']);

const { orientation, status, createdAt, content, attachments, contentAttributes } =
  useMessageContext();

const { t } = useI18n();

const gmailStatus = computed(() => {
  const attributes = contentAttributes?.value || {};
  if (!Object.prototype.hasOwnProperty.call(attributes, 'apolisGmailDelivery')) {
    return null;
  }
  const marker = attributes.apolisGmailDelivery;
  const labels = {
    preparation_required: 'Email needs operator review before preparation. Do not resend.',
    awaiting_approval: 'Awaiting approval — not sent.',
    approved: 'Approved — waiting for submission.',
    submitting: 'Submission in progress — delivery is not confirmed. Do not resend; ask an administrator to check.',
    unknown: 'Submission outcome unknown — it may have been accepted. Do not resend; ask an administrator to reconcile.',
    rejected: 'Submission rejected — ask an administrator to review it.',
    accepted_reconciliation: 'Provider accepted this email — local status needs reconciliation. Recipient delivery is not confirmed. Do not resend.',
  };
  if (marker?.version === 1 && Object.prototype.hasOwnProperty.call(labels, marker?.state)) {
    return labels[marker.state];
  }
  return 'Email status needs administrator review. Delivery is not confirmed. Do not resend.';
});

const canRetry = computed(() => {
  const hasContent = content.value !== null;
  const hasAttachments = attachments.value && attachments.value.length > 0;
  return !hasOneDayPassed(createdAt.value) && (hasContent || hasAttachments);
});
</script>

<template>
  <div
    v-if="gmailStatus"
    role="status"
    data-testid="apolis-gmail-status"
    class="text-xs text-n-ruby-11 min-w-0 max-w-full whitespace-normal break-words leading-5"
  >
    {{ gmailStatus }}
  </div>
  <div v-else class="text-xs text-n-ruby-11 flex items-center gap-1.5">
    <span>{{ t('CHAT_LIST.FAILED_TO_SEND') }}</span>
    <div class="relative group">
      <div
        class="bg-n-alpha-2 rounded-md size-5 grid place-content-center cursor-pointer"
      >
        <Icon
          icon="i-lucide-alert-triangle"
          class="text-n-ruby-11 size-[14px]"
        />
      </div>
      <div
        class="absolute bg-n-alpha-3 px-4 py-3 border rounded-xl border-n-strong text-n-slate-12 bottom-6 w-52 text-xs backdrop-blur-[100px] shadow-[0px_0px_24px_0px_rgba(0,0,0,0.12)] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all break-all"
        :class="{
          'ltr:left-0 rtl:right-0': orientation === ORIENTATION.LEFT,
          'ltr:right-0 rtl:left-0': orientation === ORIENTATION.RIGHT,
        }"
      >
        {{ error }}
      </div>
    </div>
    <button
      v-if="canRetry"
      type="button"
      :disabled="status !== MESSAGE_STATUS.FAILED"
      class="bg-n-alpha-2 rounded-md size-5 grid place-content-center cursor-pointer"
      @click="emit('retry')"
    >
      <Icon icon="i-lucide-refresh-ccw" class="text-n-ruby-11 size-[14px]" />
    </button>
  </div>
</template>
